import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import CountUp from 'react-countup';
import { useInView } from 'react-intersection-observer';
import axios from 'axios';
import API_BASE_URL, { MEDIA_BASE_URL } from '../config';
import {
  FaArrowRight,
  FaQuoteLeft,
  FaGraduationCap,
  FaStethoscope,
  FaHardHat,
  FaChevronLeft,
  FaChevronRight,
  FaBuilding,
  FaStar,
  FaGlobe,
  FaHandshake,
  FaLightbulb,
  FaShieldAlt,
  FaUsers,
} from 'react-icons/fa';
import './Home.css';

const getImageUrl = (imagePath, fallbackUrl) => {
  if (!imagePath) return fallbackUrl;
  if (imagePath.startsWith('http')) return imagePath;
  return `${MEDIA_BASE_URL}${imagePath.startsWith('/') ? '' : '/'}${imagePath}`;
};

const Home = () => {
  const [testIndex, setTestIndex] = useState(0);
  const [pageData, setPageData] = useState(null);
  const [loading, setLoading] = useState(true);

  const { ref: statsRef, inView: statsVisible } = useInView({
    threshold: 0.3,
    triggerOnce: true,
  });

  const { ref: aboutRef, inView: aboutVisible } = useInView({
    threshold: 0.2,
    triggerOnce: true,
  });

  const { ref: vertRef, inView: vertVisible } = useInView({
    threshold: 0.2,
    triggerOnce: true,
  });

  useEffect(() => {
    const fetchPublicData = async () => {
      try {
        const res = await axios.get(`${API_BASE_URL}/public/landing/`);
        setPageData(res.data);
      } catch (err) {
        console.error('Error fetching landing page data:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchPublicData();
  }, []);

  const coreValues = [
    { icon: <FaShieldAlt />, title: 'Trust & Transparency', desc: 'Honest, ethical, and open business dealings with all stakeholders.' },
    { icon: <FaStar />, title: 'Quality Excellence', desc: 'Delivering superior and uncompromising standards in every venture.' },
    { icon: <FaLightbulb />, title: 'Insight & Innovation', desc: 'Implementing smarter solutions for a future-ready enterprise.' },
    { icon: <FaHandshake />, title: 'Customer First', desc: 'Your satisfaction and growth remain our highest priority.' },
  ];

  const heroTitle = pageData?.home?.hero_title || 'Where Trust Meets Transparency';
  const heroDesc =
    pageData?.home?.hero_description ||
    'Sarwa Group is a diversified business conglomerate driving growth and empowering lives across multiple sectors. We build the future with unyielding integrity.';

  const rawHeroImg = pageData?.home?.hero_image || pageData?.home?.heroImage;
  const heroBgImage = getImageUrl(
    rawHeroImg,
    'https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?q=80&w=2070&auto=format&fit=crop'
  );

  const aboutTitle = pageData?.about?.page_title || 'Building a Diversified Legacy of Excellence';
  const aboutDesc =
    pageData?.about?.description ||
    'Sarwa Group is a diversified business conglomerate operating across multiple industries including Education, Medical, and Construction Materials. We are committed to building a brighter, smarter, and stronger future for all.';

  const rawAboutImg = pageData?.about?.about_image || pageData?.about?.aboutImage;
  const aboutImage = getImageUrl(
    rawAboutImg,
    'https://images.unsplash.com/photo-1556761175-5973dc0f32d7?q=80&w=1632&auto=format&fit=crop'
  );

  const verticals = pageData?.services?.length > 0 ? pageData.services : [];
  const testimonials = pageData?.testimonials?.length > 0 ? pageData.testimonials : [];
  const partners = pageData?.partners?.length > 0 ? pageData.partners : [];

  const estYear = pageData?.home?.year_established || 2021;
  const numVerticals = pageData?.services?.length || 5;
  const numProjects = pageData?.portfolio?.length || 150;

  let satisfactionRate = 100;
  if (pageData?.testimonials?.length > 0) {
    const totalStars = pageData.testimonials.reduce((acc, curr) => acc + (curr.rating || 5), 0);
    const avgRating = totalStars / pageData.testimonials.length;
    satisfactionRate = Math.round((avgRating / 5) * 100);
  }

  const stats = [
    { icon: <FaBuilding />, num: estYear, suffix: '', label: 'Year Established' },
    { icon: <FaGlobe />, num: numVerticals, suffix: '+', label: 'Business Verticals' },
    { icon: <FaStar />, num: numProjects, suffix: '+', label: 'Projects Delivered' },
    { icon: <FaUsers />, num: satisfactionRate, suffix: '%', label: 'Client Satisfaction' },
  ];

  const verticalIcons = [
    <FaGraduationCap />,
    <FaStethoscope />,
    <FaHardHat />,
    <FaHandshake />,
    <FaLightbulb />,
    <FaGlobe />,
  ];

  useEffect(() => {
    if (testimonials.length === 0) return;
    const timer = setInterval(() => {
      setTestIndex((prev) => (prev + 1) % testimonials.length);
    }, 6000);
    return () => clearInterval(timer);
  }, [testimonials.length]);

  const nextTest = () => setTestIndex((prev) => (prev + 1) % testimonials.length);
  const prevTest = () => setTestIndex((prev) => (prev - 1 + testimonials.length) % testimonials.length);

  if (loading) {
    return (
      <div className="sg-loader">
        <div className="sg-loader-inner">
          <div className="sg-loader-ring"></div>
          <div className="sg-loader-ring"></div>
          <div className="sg-loader-ring"></div>
          <span className="sg-loader-text">SARWA GROUP</span>
        </div>
      </div>
    );
  }

  return (
    <main className="sg-home">
      {/* ══════════════════════════════════════════
          HERO SECTION
         ══════════════════════════════════════════ */}
      <section className="sg-hero" style={{ backgroundImage: `url(${heroBgImage})` }}>
        <div className="sg-hero-overlay"></div>

        {/* Decorative Elements */}
        <div className="sg-hero-decor">
          <div className="sg-hero-line sg-hero-line-1"></div>
          <div className="sg-hero-line sg-hero-line-2"></div>
          <div className="sg-hero-circle sg-hero-circle-1"></div>
          <div className="sg-hero-circle sg-hero-circle-2"></div>
        </div>

        <div className="container sg-hero-content">
          <div className="sg-hero-text">
            <div className="sg-hero-badge">
              <span className="sg-badge-dot"></span>
              <span>SARWA GROUP OF COMPANIES</span>
              <span className="sg-badge-dot"></span>
            </div>

            <h1 className="sg-hero-title">
              {heroTitle.split(' ').map((word, i) =>
                ['Trust', 'Transparency', 'Excellence', 'Growth'].some(
                  (gw) => word.toLowerCase().includes(gw.toLowerCase())
                ) ? (
                  <span key={i} className="sg-gold-word">{word} </span>
                ) : (
                  <span key={i}>{word} </span>
                )
              )}
            </h1>

            <div className="sg-hero-separator">
              <span className="sg-sep-line"></span>
              <span className="sg-sep-diamond"></span>
              <span className="sg-sep-line"></span>
            </div>

            <p className="sg-hero-desc">{heroDesc}</p>

            <div className="sg-hero-actions">
              <Link to="/about" className="sg-btn-primary">
                <span>Discover Our Legacy</span>
                <FaArrowRight />
              </Link>
              <Link to="/services" className="sg-btn-ghost">
                <span>Explore Businesses</span>
                <FaArrowRight />
              </Link>
            </div>
          </div>
        </div>

        {/* Pillars Strip */}
        <div className="sg-pillars-strip">
          <div className="container sg-pillars-inner">
            {[
              { icon: <FaGraduationCap />, title: 'Education', sub: 'Transforming Futures' },
              { icon: <FaStethoscope />, title: 'Healthcare', sub: 'Caring for Lives' },
              { icon: <FaHardHat />, title: 'Infrastructure', sub: 'Building Tomorrow' },
            ].map((p, i) => (
              <React.Fragment key={i}>
                {i > 0 && <div className="sg-pillar-divider"></div>}
                <div className="sg-pillar-item">
                  <div className="sg-pillar-icon">{p.icon}</div>
                  <div className="sg-pillar-text">
                    <strong>{p.title}</strong>
                    <span>{p.sub}</span>
                  </div>
                </div>
              </React.Fragment>
            ))}
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════════
          STATS SECTION (Sleek Compact Single Row)
         ══════════════════════════════════════════ */}
      <section className="sg-stats" ref={statsRef}>
        <div className="container sg-stats-grid">
          {stats.map((s, i) => (
            <div
              key={i}
              className={`sg-stat-card ${statsVisible ? 'sg-animate-in' : ''}`}
              style={{ animationDelay: `${i * 0.15}s` }}
            >
              <div className="sg-stat-icon">{s.icon}</div>
              <h3 className="sg-stat-number">
                {statsVisible ? <CountUp end={s.num} duration={2.5} /> : 0}
                <span className="sg-stat-suffix">{s.suffix}</span>
              </h3>
              <p className="sg-stat-label">{s.label}</p>
              <div className="sg-stat-underline"></div>
            </div>
          ))}
        </div>
      </section>

      {/* ══════════════════════════════════════════
          ABOUT SECTION
         ══════════════════════════════════════════ */}
      <section className={`sg-about section-padding ${aboutVisible ? 'sg-visible' : ''}`} ref={aboutRef}>
        <div className="container sg-about-grid">
          {/* Image Side */}
          <div className="sg-about-visual">
            <div className="sg-about-img-wrapper">
              <div className="sg-about-img-accent"></div>
              <img src={aboutImage} alt="Sarwa Group Corporate" className="sg-about-img" />
              <div className="sg-about-floating-badge">
                <div className="sg-afb-icon"><FaStar /></div>
                <div>
                  <strong>Excellence</strong>
                  <span>Across Sectors</span>
                </div>
              </div>
            </div>
            <div className="sg-about-dots-pattern"></div>
          </div>

          {/* Content Side */}
          <div className="sg-about-content">
            <span className="sg-section-tag">WHO WE ARE</span>
            <h2 className="sg-section-heading">{aboutTitle}</h2>
            <div className="sg-heading-line"></div>
            <p className="sg-about-desc">{aboutDesc}</p>

            <div className="sg-values-grid">
              {coreValues.map((v, i) => (
                <div key={i} className="sg-value-item">
                  <div className="sg-value-icon">{v.icon}</div>
                  <div>
                    <h4>{v.title}</h4>
                    <p>{v.desc}</p>
                  </div>
                </div>
              ))}
            </div>

            <Link to="/about" className="sg-link-arrow">
              Read Our Complete Story
              <span className="sg-arrow-line"></span>
              <FaArrowRight />
            </Link>
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════════
          BUSINESS VERTICALS
         ══════════════════════════════════════════ */}
      {verticals.length > 0 && (
        <section className={`sg-verticals section-padding ${vertVisible ? 'sg-visible' : ''}`} ref={vertRef}>
          <div className="sg-verticals-bg"></div>
          <div className="container">
            <div className="sg-section-header-center">
              <span className="sg-section-tag">OUR PORTFOLIO</span>
              <h2 className="sg-section-heading">Group Companies & Ventures</h2>
              <p className="sg-section-subtext">
                Sarwa Group operates across diverse sectors, each contributing to our vision of sustainable excellence.
              </p>
            </div>

            <div className="sg-verticals-grid">
              {verticals.map((v, i) => (
                <div key={i} className="sg-vertical-card" style={{ animationDelay: `${i * 0.1}s` }}>
                  <div className="sg-vc-header">
                    <div className="sg-vc-icon">
                      {verticalIcons[i % verticalIcons.length]}
                    </div>
                    <span className="sg-vc-num">{String(i + 1).padStart(2, '0')}</span>
                  </div>
                  <h3 className="sg-vc-title">{v.title}</h3>
                  <p className="sg-vc-desc">{v.description}</p>
                  <div className="sg-vc-footer">
                    <Link
                      to={`/services/${v.title.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
                      className="sg-vc-link"
                    >
                      <span>Explore Division</span>
                      <FaArrowRight />
                    </Link>
                  </div>
                  <div className="sg-vc-accent-line"></div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ══════════════════════════════════════════
          TESTIMONIALS
         ══════════════════════════════════════════ */}
      {testimonials.length > 0 && (
        <section className="sg-testimonials section-padding">
          <div className="sg-testi-bg-pattern"></div>
          <div className="container">
            <div className="sg-testi-wrapper">
              <div className="sg-testi-left">
                <span className="sg-section-tag light">TESTIMONIALS</span>
                <h2 className="sg-section-heading light">
                  What Our <span className="sg-gold-word">Partners</span> Say
                </h2>
                <div className="sg-testi-nav">
                  <button onClick={prevTest} className="sg-testi-nav-btn" aria-label="Previous">
                    <FaChevronLeft />
                  </button>
                  <span className="sg-testi-counter">
                    {String(testIndex + 1).padStart(2, '0')} / {String(testimonials.length).padStart(2, '0')}
                  </span>
                  <button onClick={nextTest} className="sg-testi-nav-btn" aria-label="Next">
                    <FaChevronRight />
                  </button>
                </div>
              </div>

              <div className="sg-testi-right">
                <div className="sg-testi-quote-icon">
                  <FaQuoteLeft />
                </div>
                <div className="sg-testi-slider">
                  {testimonials.map((t, i) => (
                    <div
                      key={i}
                      className={`sg-testi-slide ${testIndex === i ? 'active' : ''}`}
                    >
                      <p className="sg-testi-text">"{t.testimonial_text}"</p>
                      <div className="sg-testi-author">
                        <div className="sg-testi-author-avatar">
                          {(t.client_name || 'C').charAt(0)}
                        </div>
                        <div>
                          <strong>{t.client_name}</strong>
                          <span>{t.role_company}</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="sg-testi-dots">
                  {testimonials.map((_, i) => (
                    <button
                      key={i}
                      className={`sg-testi-dot ${testIndex === i ? 'active' : ''}`}
                      onClick={() => setTestIndex(i)}
                      aria-label={`Slide ${i + 1}`}
                    />
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* ══════════════════════════════════════════
          PARTNERS
         ══════════════════════════════════════════ */}
      {partners.length > 0 && (
        <section className="sg-partners">
          <div className="container">
            <p className="sg-partners-label">TRUSTED BY INDUSTRY LEADERS</p>
            <div className="sg-partners-track-wrap">
              <div className="sg-partners-track">
                {[...partners, ...partners, ...partners].map((p, i) => (
                  <div key={i} className="sg-partner-item">
                    <span>{p.name || p}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>
      )}
    </main>
  );
};

export default Home;